import React, { useState } from 'react';
import api from '../lib/api';
import ModalShell, { inputClass, selectClass, labelClass, requiredMark, errorBoxClass } from './ModalShell';

const reasons = [
  'Manual Adjustment',
  'Purchase',
  'Sale',
  'Return',
  'Damage',
  'Loss',
  'Found',
  'Other'
];

const STOCK_SCALE = 1000000n;

const parseScaledDecimal = (value) => {
  const text = String(value).trim();
  const match = /^([+-]?)(\d+)(?:\.(\d*))?$/.exec(text);
  if (!match) return null;

  const fractionalPart = match[3] || '';
  if (fractionalPart.length > 6) return null;

  const whole = BigInt(match[2]);
  const fraction = BigInt(fractionalPart.padEnd(6, '0') || '0');
  const magnitude = (whole * STOCK_SCALE) + fraction;
  return match[1] === '-' ? -magnitude : magnitude;
};

const formatScaledDecimal = (value) => {
  const isNegative = value < 0n;
  const magnitude = isNegative ? -value : value;
  const whole = magnitude / STOCK_SCALE;
  const fraction = (magnitude % STOCK_SCALE).toString().padStart(6, '0');
  return `${isNegative ? '-' : ''}${whole}.${fraction}`;
};

const calculateStockPreview = (currentStock, quantityChange) => {
  const current = parseScaledDecimal(currentStock);
  const change = parseScaledDecimal(quantityChange);
  if (current == null || change == null || current < 0n) return null;
  return { change, final: current + change };
};

const StockAdjustmentModal = ({ product, onClose, onSuccess }) => {
  const [quantityChange, setQuantityChange] = useState('');
  const [reason, setReason] = useState('Manual Adjustment');
  const [reference, setReference] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    const preview = calculateStockPreview(product.current_stock, quantityChange);
    if (preview == null) {
      setError('Enter a valid quantity with at most six decimal places');
      return;
    }
    if (preview.change === 0n) {
      setError('Quantity change cannot be zero');
      return;
    }
    if (preview.final < 0n) {
      const magnitude = preview.change < 0n ? -preview.change : preview.change;
      setError(`Cannot reduce stock by ${formatScaledDecimal(magnitude)}. Current stock is only ${product.current_stock}`);
      return;
    }
    setLoading(true);
    try {
      await api.post(`/products/${product.id}/adjust-stock`, {
        quantity_change: quantityChange,
        reason: reason,
        reference: reference || null
      });
      onSuccess();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to adjust stock');
    } finally {
      setLoading(false);
    }
  };

  const preview = quantityChange
    ? calculateStockPreview(product.current_stock, quantityChange)
    : null;
  const newStock = preview == null ? null : formatScaledDecimal(preview.final);

  return (
    <ModalShell
      title="Adjust Stock"
      onClose={onClose}
      maxWidth="max-w-md"
      dataTestId="stock-adjustment-modal"
      footer={
        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 border border-[#d1d5db] rounded-lg text-[#1a1a2e] hover:bg-white font-medium transition-colors"
            data-testid="cancel-adjustment-button"
          >
            Cancel
          </button>
          <button
            type="submit"
            form="adjust-form"
            disabled={loading}
            className="px-4 py-2 bg-gradient-to-r from-[#22B8D4] via-[#4A7AE0] to-[#7C3BD6] text-white rounded-lg font-medium transition-colors disabled:opacity-50"
            data-testid="submit-adjustment-button"
          >
            {loading ? 'Adjusting...' : 'Adjust Stock'}
          </button>
        </div>
      }
    >
      <form id="adjust-form" onSubmit={handleSubmit}>
        {error && <div className={errorBoxClass} data-testid="adjustment-error">{error}</div>}

        <div className="mb-4 p-4 bg-white rounded-lg border border-[#d1d5db]">
          <div className="flex justify-between items-center mb-2">
            <span className="text-sm font-medium text-[#1a1a2e]">Product:</span>
            <span className="text-sm text-[#6b7280]">{product.name}</span>
          </div>
          <div className="flex justify-between items-center mb-2">
            <span className="text-sm font-medium text-[#1a1a2e]">SKU:</span>
            <span className="text-sm text-[#6b7280]">{product.sku}</span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-sm font-medium text-[#1a1a2e]">Current Stock:</span>
            <span className="text-sm font-semibold text-[#4A7AE0]">{product.current_stock} {product.unit}</span>
          </div>
        </div>

        <div className="mb-4">
          <label className={labelClass}>
            Quantity Change {requiredMark}
            <span className="text-xs text-[#6b7280] font-normal ml-2">(positive to add, negative to remove)</span>
          </label>
          <input
            type="number"
            step="0.000001"
            className={inputClass}
            value={quantityChange}
            onChange={(e) => setQuantityChange(e.target.value)}
            placeholder="e.g., 10 or -5"
            required
            data-testid="quantity-change-input"
          />
        </div>

        {quantityChange && newStock != null && (
          <div className="mb-4 p-3 bg-[#EFF6FF] border border-[#BFDBFE] rounded-lg">
            <p className="text-sm text-[#1E40AF]">
              New Stock: <span className="font-semibold">{newStock} {product.unit}</span>
            </p>
          </div>
        )}

        <div className="mb-4">
          <label className={labelClass}>Reason {requiredMark}</label>
          <select
            className={selectClass}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            required
            data-testid="reason-select"
          >
            {reasons.map(r => <option key={r} value={r}>{r}</option>)}
          </select>
        </div>

        <div>
          <label className={labelClass}>
            Reference
            <span className="text-xs text-[#6b7280] font-normal ml-2">(optional, e.g., invoice #, PO #)</span>
          </label>
          <input
            type="text"
            className={inputClass}
            value={reference}
            onChange={(e) => setReference(e.target.value)}
            placeholder="e.g., INV-1001"
            data-testid="reference-input"
          />
        </div>
      </form>
    </ModalShell>
  );
};

export default StockAdjustmentModal;
